import { MAX_STRUCTURED_CSV_SIZE, parseCsvRows } from "./structured-csv";

/**
 * Lenient CSV → 2D MATRIX parse, for the `data` variable's spreadsheet.
 *
 * A data variable's value is positional (see data-grid.ts): the whole table or
 * chart is populated from the matrix, and there are no column NAMES to bind
 * to. So none of `parseStructuredCsv`'s record-shaped rules apply here —
 * exports from Excel/Sheets routinely carry an empty first header cell (a
 * row-label column), repeated header labels, blank spacer rows and short
 * rows, and every one of those is legitimate table content.
 *
 * What this keeps from the strict parser: the RFC-4180 tokenizer (quoted
 * commas/newlines, `""` escapes), the leading BOM strip and the 2 MB cap.
 *
 * What it does differently:
 * - no header row is required, and headers may be empty or duplicated;
 * - empty cells and interior empty rows are preserved — a gap inside a table
 *   is real content;
 * - trailing fully-empty rows are dropped (a trailing newline, or blank lines
 *   padding the end of the file, are file artifacts, not rows);
 * - ragged rows are padded with empty cells to the widest row, so the grid is
 *   rectangular instead of throwing.
 */
export const parseMatrixCsv = (input: string): string[][] => {
  const rows = parseCsvRows(input.replace(/^\uFEFF/, ""), true);

  let last = rows.length - 1;
  while (last >= 0 && rows[last].every((cell) => cell === "")) last -= 1;
  const kept = rows.slice(0, last + 1);
  if (!kept.length) return [];

  const width = kept.reduce((widest, row) => Math.max(widest, row.length), 0);
  return kept.map((row) => {
    const padded = row.slice();
    while (padded.length < width) padded.push("");
    return padded;
  });
};

/** `parseMatrixCsv` for an uploaded file, with the shared 2 MB size guard. */
export const readMatrixCsvFile = async (file: File): Promise<string[][]> => {
  if (file.size > MAX_STRUCTURED_CSV_SIZE) {
    throw new Error("CSV file must be smaller than 2 MB");
  }
  return parseMatrixCsv(await file.text());
};
